import React from "react";
import { toast } from "sonner";
import { Facebook, Link2, Linkedin, Mail, MessageCircle, Share2 } from "lucide-react";
import { t } from "@/i18n";

/* Share links, not share widgets. Each platform's own intent URL is a plain
   anchor: nothing is embedded, so no third party gets to watch the page load
   in order to offer a button. */

const TARGETS = [
  {
    id: "whatsapp",
    Icon: MessageCircle,
    label: "WhatsApp",
    build: ({ url, title }) => `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
  },
  {
    id: "facebook",
    Icon: Facebook,
    label: "Facebook",
    build: ({ url }) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    id: "x",
    // X has no lucide glyph; its own share endpoint is still twitter.com
    Icon: Share2,
    label: "X",
    build: ({ url, title }) =>
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  {
    id: "linkedin",
    Icon: Linkedin,
    label: "LinkedIn",
    build: ({ url }) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
  {
    id: "email",
    Icon: Mail,
    label: "Email",
    build: ({ url, title }) =>
      `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`,
  },
];

export const ShareRow = ({ url, title = "", className = "" }) => {
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("site.copied"));
    } catch {
      // clipboard access is refused outside a secure context; showing the URL
      // still lets someone copy it by hand
      toast.message(shareUrl);
    }
  };

  const native = async () => {
    if (!navigator.share) return false;
    try {
      await navigator.share({ title, url: shareUrl });
      return true;
    } catch {
      return true; // the reader cancelled; that is not an error to report
    }
  };

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="share-row">
      <span className="mr-1 text-[11.5px] uppercase tracking-[0.1em] text-[color:var(--muted-ink)]">
        {t("site.share")}
      </span>

      {TARGETS.map(({ id, Icon, label, build }) => (
        <a
          key={id}
          href={build({ url: shareUrl, title })}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          data-testid={`share-${id}`}
          onClick={async (event) => {
            // on a phone, the system sheet is better than a new tab
            if (window.innerWidth < 640 && (await native())) event.preventDefault();
          }}
          className="rounded-full border p-2 transition-colors hover:bg-[color:var(--paper-2)]"
          style={{ borderColor: "var(--line)" }}
        >
          <Icon className="h-3.5 w-3.5" style={{ color: "var(--ink-soft)" }} />
        </a>
      ))}

      <button
        type="button"
        onClick={copy}
        aria-label={t("site.copyLink")}
        title={t("site.copyLink")}
        data-testid="share-copy"
        className="rounded-full border p-2 transition-colors hover:bg-[color:var(--paper-2)]"
        style={{ borderColor: "var(--line)" }}
      >
        <Link2 className="h-3.5 w-3.5" style={{ color: "var(--ink-soft)" }} />
      </button>
    </div>
  );
};

export default ShareRow;
